/**
 * CMS image URLs.
 *
 * Stored URLs always point at the **original** upload — a full-resolution JPEG
 * that gallery guests can download in print quality. Displaying that same file
 * in a grid tile costs megabytes per photo, so display URLs are routed through
 * the backend derivative endpoint (`/api/img/<width>/<format>/<path>`), which
 * resizes and re-encodes on first request and caches the result on disk.
 *
 * Because derivatives are computed from the stored original, existing events
 * need no re-upload and no data migration — the stored URL stays as it is.
 */
export type CmsImageStyle = 'thumb' | 'md' | 'lg' | 'full'

/** Must stay in sync with DERIVATIVE_WIDTHS in the backend service. */
export const CMS_IMAGE_WIDTHS = [160, 320, 480, 640, 960, 1280, 1600, 1920] as const
export type CmsImageWidth = (typeof CMS_IMAGE_WIDTHS)[number]

export const CMS_STYLE_WIDTH: Record<CmsImageStyle, CmsImageWidth> = {
  thumb: 320,
  md: 640,
  lg: 1280,
  full: 1920,
}

/** Hosts whose absolute media URLs point at our own uploads volume. */
const OWN_MEDIA_HOSTS = new Set(['bnkr.ch', 'www.bnkr.ch', 'hub.bnkr.ch', 'bunker-events.ch', 'www.bunker-events.ch'])

/** sharp can resize these; GIF/SVG are served untouched. */
const RESIZABLE = /\.(jpe?g|png|webp|avif|tiff?)$/i

/**
 * Normalise a stored URL for display.
 *
 * Older records (and the previous upload code) stored gallery photos under
 * `/assets/event-photos/…`, whose Caddy route was added later and may not be
 * loaded in the running reverse proxy — those URLs 404. Both paths resolve to
 * the same file on the media volume, so rewrite to `/media/event-photos/…`
 * which is known to be served.
 */
function normalizeStoredUrl(url: string): string {
  return url.trim().replace(/^\/assets\/event-photos\//, '/media/event-photos/')
}

function encodeSegment(segment: string): string {
  let raw = segment
  try {
    raw = decodeURIComponent(segment)
  } catch {
    /* malformed escape — encode the literal text */
  }
  return encodeURIComponent(raw)
}

/**
 * Path of a stored image relative to the media root (`event-photos/foo.jpg`),
 * or null when the URL points somewhere we cannot derive from (external host,
 * data URI, non-raster format).
 */
function mediaRelativePath(url: string): string | null {
  let p = normalizeStoredUrl(url)
  if (!p) return null

  if (/^https?:\/\//i.test(p)) {
    try {
      const parsed = new URL(p)
      if (!OWN_MEDIA_HOSTS.has(parsed.hostname)) return null
      p = parsed.pathname
    } catch {
      return null
    }
  }

  if (p.startsWith('/assets/event-photos/')) p = `/media/${p.slice('/assets/'.length)}`
  if (!p.startsWith('/media/')) return null

  const rel = p.slice('/media/'.length).split(/[?#]/)[0]
  if (!rel || rel.includes('..')) return null
  if (!RESIZABLE.test(rel)) return null
  return rel
}

/** URL of a resized/re-encoded variant, or null when the source cannot be derived. */
export function cmsImageVariantUrl(
  url: string | null | undefined,
  width: CmsImageWidth,
  format: 'webp' | 'jpg' = 'webp',
): string | null {
  if (!url?.trim()) return null
  const rel = mediaRelativePath(url)
  if (!rel) return null
  const encoded = rel.split('/').map(encodeSegment).join('/')
  return `/api/img/${width}/${format}/${encoded}`
}

/**
 * Resolve a stored CMS image URL for **display**. Returns an optimised
 * derivative when possible and falls back to the stored file otherwise
 * (external images, SVG/GIF, or anything outside the media volume).
 */
export function cmsImageUrl(
  url: string | null | undefined,
  style: CmsImageStyle = 'md',
): string | null {
  if (!url?.trim()) return null
  return cmsImageVariantUrl(url, CMS_STYLE_WIDTH[style]) ?? normalizeStoredUrl(url)
}

/**
 * The untouched stored file — full resolution, original encoding. Used for
 * downloads, where guests want print quality rather than a small file.
 */
export function cmsImageOriginalUrl(url: string | null | undefined): string | null {
  if (!url?.trim()) return null
  return normalizeStoredUrl(url)
}

/**
 * A `srcset` across the given widths so the browser picks the variant that
 * matches the device pixel ratio. Returns undefined when the image cannot be
 * derived, in which case callers should just render `src`.
 */
export function cmsImageSrcSet(
  url: string | null | undefined,
  widths: readonly CmsImageWidth[],
  format: 'webp' | 'jpg' = 'webp',
): string | undefined {
  if (!url?.trim()) return undefined
  const entries = widths
    .map((w) => {
      const variant = cmsImageVariantUrl(url, w, format)
      return variant ? `${variant} ${w}w` : null
    })
    .filter((v): v is string => v !== null)
  return entries.length ? entries.join(', ') : undefined
}

/**
 * Social/preview image: JPEG rather than WebP, since some link unfurlers still
 * choke on WebP, and capped at 1280px so previews stay well under scraper size
 * limits.
 */
export function cmsImageSocialUrl(url: string | null | undefined): string | null {
  if (!url?.trim()) return null
  return cmsImageVariantUrl(url, 1280, 'jpg') ?? normalizeStoredUrl(url)
}

/**
 * Next URL to try when an image fails to load, or null when nothing is left.
 *
 * Two known failure modes, applied in order:
 *  1. A derivative URL (`/api/img/…`) — the backend may predate this endpoint,
 *     so retry the stored original it was derived from.
 *  2. A legacy WebP variant (`…-md.webp`) from the old upload pipeline, whose
 *     variants may 404 — retry the JPEG original (`…-original.jpg`) that is
 *     the file we actually keep.
 */
export function cmsImageFallbackUrl(url: string | null | undefined): string | null {
  const raw = url?.trim()
  if (!raw) return null

  const path = raw.replace(/^https?:\/\/[^/]+/i, '')
  const derivative = path.match(/^\/api\/img\/\d+\/(?:webp|jpg)\/(.+)$/)
  if (derivative) return `/media/${derivative[1]}`

  const legacyVariant = raw.match(/^(.*)-(?:thumb|md|lg|full)\.webp(\?.*)?$/i)
  if (legacyVariant) return `${legacyVariant[1]}-original.jpg${legacyVariant[2] ?? ''}`

  return null
}
